'use client';

import styled from '@emotion/styled';
import { getColorCode } from './color-map';

export interface ColorOption {
  name: string;
  value?: string;
}

export interface ColorSelectorProps {
  colors: ColorOption[];
  selectedColor?: string;
  onColorChange: (colorName: string) => void;
  maxVisible?: number;
  size?: 'sm' | 'md';
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: nowrap;
`;

interface SwatchProps {
  $selected: boolean;
  $size: 'sm' | 'md';
}

const SWATCH_SIZES = { sm: 20, md: 36 } as const;

const Swatch = styled.button<SwatchProps>`
  width: ${({ $size }) => SWATCH_SIZES[$size]}px;
  height: ${({ $size }) => SWATCH_SIZES[$size]}px;
  border-radius: ${({ theme }) => theme.radius.full};
  border: ${({ $selected, theme }) =>
    $selected
      ? `2px solid ${theme.colors.text.primary}`
      : `1px solid ${theme.colors.border.color}`};
  cursor: pointer;
  box-shadow: ${({ $selected, theme }) =>
    $selected
      ? `0 0 0 2px ${theme.colors.background.primary}, 0 0 0 3px ${theme.colors.text.primary}`
      : 'none'};
  transition: all 0.15s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const OverflowIndicator = styled.span<{ $size: 'sm' | 'md' }>`
  width: ${({ $size }) => SWATCH_SIZES[$size]}px;
  height: ${({ $size }) => SWATCH_SIZES[$size]}px;
  border-radius: ${({ theme }) => theme.radius.full};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  background: ${({ theme }) => theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ $size }) => ($size === 'sm' ? 9 : 11)}px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
  flex-shrink: 0;
`;

export function ColorSelector({
  colors,
  selectedColor,
  onColorChange,
  maxVisible,
  size = 'md',
}: Readonly<ColorSelectorProps>) {
  if (!colors || colors.length <= 1) return null;

  const visibleColors = maxVisible ? colors.slice(0, maxVisible) : colors;
  const overflowCount = maxVisible ? Math.max(0, colors.length - maxVisible) : 0;

  return (
    <Container
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {visibleColors.map((color) => (
        <Swatch
          key={color.name}
          $selected={selectedColor === color.name}
          $size={size}
          title={color.name}
          style={{ backgroundColor: getColorCode(color.value || color.name) }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onColorChange(color.name);
          }}
        />
      ))}
      {overflowCount > 0 && (
        <OverflowIndicator $size={size}>+{overflowCount}</OverflowIndicator>
      )}
    </Container>
  );
}

export default ColorSelector;
